import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  useLoadDeletedForms,
  useLoadForms,
  useLoadSetting,
  useLoadSubmissionLinkHashes,
} from "@smart/manage-gql-client-dom";
import { useManageFormsContext, useUser } from "@smart/manage-hooks-dom";

import { ListForms } from "../../components/embedded/form-builder/list-forms";
import { startReviewView } from "../../components/embedded/form-builder/list-forms/review-view";
import { reminderMessageFilterSchema } from "../../components/embedded/form-builder/list-forms/storage-keys";
import { filterDisplayableForms } from "../../components/embedded/shared/custom-reminder-message";
import { RESTORE_DELETED_FORM_TIMEOUT } from "../../components/embedded/types";

const REGULAR_POLL_INTERVAL = 60000; // 1 minute
const RESTORING_POLL_INTERVAL = 5000; // 5 seconds

const reminderMessageParam = "reminderMessage";

const useReminderMessageParam = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ hasParam, applied }] = useState(() => {
    const parsed = reminderMessageFilterSchema.safeParse(
      searchParams.get(reminderMessageParam),
    );
    if (parsed.success) startReviewView(parsed.data);
    return {
      hasParam: searchParams.has(reminderMessageParam),
      applied: parsed.success,
    };
  });

  useEffect(() => {
    if (!hasParam) return;
    setSearchParams(
      (params) => {
        params.delete(reminderMessageParam);
        return params;
      },
      { replace: true },
    );
  }, [hasParam]);

  return applied;
};

const BuilderList = () => {
  const arrivedWithReminderMessageFilter = useReminderMessageParam();
  const { permission } = useUser();
  const { actingTeam } = useManageFormsContext();
  const setting = useLoadSetting({});
  const [pollInterval, setPollInterval] = useState(REGULAR_POLL_INTERVAL);
  const forms = useLoadForms(
    {
      teamUri: actingTeam?.uri,
      includeGlobal: true,
    },
    { fetchPolicy: "cache-and-network", pollInterval },
  );
  const deletedForms = useLoadDeletedForms(
    {
      teamUri: actingTeam?.uri,
    },
    { fetchPolicy: "cache-and-network", pollInterval },
  );
  const submissionLinkHashes = useLoadSubmissionLinkHashes(
    {},
    { fetchPolicy: "cache-and-network", pollInterval },
  );

  useEffect(() => {
    const isRestoring = deletedForms.result?.some(
      (form) =>
        form.restoredAt &&
        new Date(form.restoredAt) >
          new Date(Date.now() - RESTORE_DELETED_FORM_TIMEOUT),
    );
    if (isRestoring && pollInterval === REGULAR_POLL_INTERVAL) {
      setPollInterval(RESTORING_POLL_INTERVAL);
    } else if (!isRestoring && pollInterval === RESTORING_POLL_INTERVAL) {
      setPollInterval(REGULAR_POLL_INTERVAL);
    }
  }, [deletedForms.result]);

  const formsToDisplay = useMemo(
    () =>
      filterDisplayableForms({
        forms: forms.result,
        setting: setting.result,
        permission,
      }),
    [forms.result, setting.result, permission],
  );

  return (
    <ListForms
      loading={
        forms.loading ||
        setting.loading ||
        deletedForms.loading ||
        submissionLinkHashes.loading
      }
      forms={formsToDisplay}
      deletedForms={deletedForms.result}
      setting={setting.result || undefined}
      submissionLinkHashes={submissionLinkHashes.result}
      highlightReminderMessageFilter={arrivedWithReminderMessageFilter}
    />
  );
};

export { BuilderList };
